import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, FileText, Printer, FileCheck, RotateCcw, RefreshCw, Replace, Pencil, Trash2, Search, Calendar, CreditCard, MessageCircle, UserPlus, X, ChevronDown, Check, ShieldCheck, Mail, Phone, Send, Copy, Lock, Unlock, Banknote, Camera, ReceiptText, Sparkles, Upload, Paperclip, Loader2, Brain } from "lucide-react";
import { api, API } from "../../lib/api";
import { toast } from "sonner";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import LocationMap from "../../components/LocationMap";
import DocumentCapture from "../../components/DocumentCapture";

const STATO = {
  attivo: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  concluso: "bg-white/10 text-white/60 border-white/20",
  prorogato: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  sostituito: "bg-blue-500/15 text-blue-300 border-blue-500/30",
};

const empty = {
  customer_id: "", car_id: "", data_inizio: "", data_fine: "",
  ora_consegna: "", ora_riconsegna: "",
  km_uscita: 0, luogo_ritiro: "", luogo_riconsegna: "",
  costo_totale: 0, acconto: 0, metodo_pagamento: "contanti", note: "",
  lingua: "it",
  // Override franchigie per singolo contratto (null = usa default Settings)
  franchigia_rca: null, franchigia_furto: null, franchigia_incendio: null, franchigia_kasko: null,
  deposito_cauzionale: 0,
};

const emptyQuickCustomer = {
  tipo: "privato",
  nome: "", cognome: "", ragione_sociale: "",
  email: "", telefono: "",
  codice_fiscale: "", piva: "",
  indirizzo: "", citta: "", cap: "", provincia: "", nazione: "Italia",
  lat: null, lng: null,
  patente_numero: "", patente_emissione: "", patente_scadenza: "", patente_comune: "", patente_categoria: "B",
  ci_numero: "", ci_scadenza: "", ci_comune: "",
  rischio: "basso", note: "",
};

export default function Contracts() {
  const [list, setList] = useState([]);
  const [cars, setCars] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [showRientro, setShowRientro] = useState(null);
  const [showProroga, setShowProroga] = useState(null);
  const [showSost, setShowSost] = useState(null);
  // Firma OTP
  const [showSign, setShowSign] = useState(null); // contract
  const [signChannel, setSignChannel] = useState("whatsapp");
  const [signInit, setSignInit] = useState(null); // { otp, sign_token, expires_at }
  const [otpInput, setOtpInput] = useState("");
  const [signLoading, setSignLoading] = useState(false);
  // Quick add customer
  const [showQuickCust, setShowQuickCust] = useState(false);
  const [qc, setQc] = useState(emptyQuickCustomer);
  const [qcSaving, setQcSaving] = useState(false);
  // Customer combobox
  const [custSearch, setCustSearch] = useState("");
  const [custOpen, setCustOpen] = useState(false);
  const [showOcrCapture, setShowOcrCapture] = useState(false);  // OCR modal per quick-add cliente

  // applica risultato OCR al form quick cliente
  const applyOcrToQc = (e) => {
    setQc((prev) => ({
      ...prev,
      nome: e.nome || prev.nome,
      cognome: e.cognome || prev.cognome,
      codice_fiscale: (e.codice_fiscale || prev.codice_fiscale || "").toUpperCase(),
      patente_numero: e.numero_documento || prev.patente_numero,
      patente_scadenza: e.data_scadenza || prev.patente_scadenza,
      patente_emissione: e.data_emissione || prev.patente_emissione,
      patente_comune: e.emittente || prev.patente_comune,
      patente_categoria: e.patente_categoria || prev.patente_categoria,
      indirizzo: e.indirizzo_residenza || prev.indirizzo,
      citta: e.citta_residenza || prev.citta,
      cap: e.cap_residenza || prev.cap,
      provincia: e.provincia_residenza || prev.provincia,
      note: prev.note + (e.data_nascita ? `\nNato il ${e.data_nascita}` : "") + (e.luogo_nascita ? ` a ${e.luogo_nascita}` : ""),
    }));
  };

  const load = async () => {
    const [c, ca, cu] = await Promise.all([api.get("/contracts"), api.get("/cars"), api.get("/customers")]);
    setList(c.data); setCars(ca.data); setCustomers(cu.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = list.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [c.numero, c.car?.targa, c.customer?.nome, c.customer?.cognome, c.customer?.ragione_sociale]
      .filter(Boolean).some((x) => x.toLowerCase().includes(s));
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const pendingAtts = form.pending_attachments || [];
      const payload = {
        ...form,
        costo_totale: +form.costo_totale,
        acconto: +form.acconto,
        km_uscita: +form.km_uscita,
        // converti null/stringa vuota → null per le franchigie override
        franchigia_rca: form.franchigia_rca === "" || form.franchigia_rca === null ? null : +form.franchigia_rca,
        franchigia_furto: form.franchigia_furto === "" || form.franchigia_furto === null ? null : +form.franchigia_furto,
        franchigia_incendio: form.franchigia_incendio === "" || form.franchigia_incendio === null ? null : +form.franchigia_incendio,
        franchigia_kasko: form.franchigia_kasko === "" || form.franchigia_kasko === null ? null : +form.franchigia_kasko,
        deposito_cauzionale: +(form.deposito_cauzionale || 0),
        prezzo_giornaliero_override: form.prezzo_giornaliero_override === "" || form.prezzo_giornaliero_override === null
          ? null : +form.prezzo_giornaliero_override,
      };
      delete payload.pending_attachments;
      let cid = editId;
      if (editId) await api.put(`/contracts/${editId}`, payload);
      else {
        const r = await api.post("/contracts", payload);
        cid = r.data?.id;
      }
      // Carica allegati pendenti
      if (cid && pendingAtts.length > 0) {
        const fd = new FormData();
        for (const a of pendingAtts) {
          const bin = Uint8Array.from(atob(a.data_b64), (c) => c.charCodeAt(0));
          fd.append("files", new Blob([bin], { type: a.content_type }), a.filename);
        }
        await api.post(`/contracts/${cid}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(editId ? "Contratto aggiornato" : "Contratto creato");
      setShowForm(false); setForm(empty); setEditId(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  // ============ AI DOCUMENT SCANNER ============
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  const onScanFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList).slice(0, 10);
    setScanning(true);
    setScanResult(null);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      fd.append("context", "contracts");
      const r = await api.post("/contracts/scan-documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { bozza, persona, attachments } = r.data || {};
      setScanResult(r.data);
      if (r.data?.smart_router?.mismatch) toast.warning(r.data.smart_router.message, { duration: 8000 });
      // Apply bozza ai campi form
      setForm((prev) => ({
        ...prev,
        customer_id: bozza?.customer_id || prev.customer_id,
        car_id:      bozza?.car_id      || prev.car_id,
        data_inizio: bozza?.data_inizio || prev.data_inizio,
        data_fine:   bozza?.data_fine   || prev.data_fine,
        costo_totale: bozza?.costo_totale || prev.costo_totale,
        acconto:      bozza?.acconto || prev.acconto,
        luogo_ritiro: bozza?.luogo_ritiro || prev.luogo_ritiro,
        luogo_riconsegna: bozza?.luogo_riconsegna || prev.luogo_riconsegna,
        pending_attachments: [...(prev.pending_attachments || []), ...(attachments || [])],
      }));
      toast.success(`AI ha estratto dati da ${files.length} documento/i`);
      // Se persona auto-creata, informa l'operatore
      if (bozza?.customer_created) {
        toast.success(`✨ Nuovo cliente "${persona?.nome || ''} ${persona?.cognome || ''}" creato in anagrafica`);
        // ricarica customers per averlo nel dropdown
        try { const r2 = await api.get("/customers"); setCustomers(r2.data); } catch {}
      } else if (!bozza?.customer_id && persona?.nome && persona?.codice_fiscale) {
        toast.info(`Cliente "${persona.nome} ${persona.cognome}" non in anagrafica — usa "Nuovo" per crearlo`);
      }
    } catch (e) {
      toast.error("Errore AI: " + (e?.response?.data?.detail || e.message));
    } finally { setScanning(false); }
  };

  const removePendingAtt = (idx) => {
    setForm((p) => ({ ...p, pending_attachments: p.pending_attachments.filter((_, i) => i !== idx) }));
  };

  // Auto-update costo_totale quando cambia veicolo o data o override prezzo
  useEffect(() => {
    if (!form.car_id || !form.data_inizio || !form.data_fine) return;
    const car = cars.find((c) => c.id === form.car_id);
    if (!car) return;
    const pg = form.prezzo_giornaliero_override != null && form.prezzo_giornaliero_override !== ""
      ? +form.prezzo_giornaliero_override : (car.prezzo_giornaliero || 0);
    const s = new Date(form.data_inizio); const e = new Date(form.data_fine);
    const giorni = Math.max(1, Math.round((e - s) / 86400000));
    const tot = +(pg * giorni).toFixed(2);
    // Solo aggiorna se il valore corrente è 0 o se il prezzo override è cambiato (per evitare loop)
    if (!editId && (+form.costo_totale === 0 || form._auto_total)) {
      setForm((p) => ({ ...p, costo_totale: tot, _auto_total: true }));
    }
  }, [form.car_id, form.data_inizio, form.data_fine, form.prezzo_giornaliero_override]); // eslint-disable-line

  const remove = async (id) => {
    if (!window.confirm("Eliminare contratto?")) return;
    await api.delete(`/contracts/${id}`);
    toast.success("Eliminato"); load();
  };

  const openPDF = (id, tipo) => {
    const url = `${API}/contracts/${id}/pdf?tipo=${tipo}`;
    // Cookie httpOnly viene inviato automaticamente con credentials: 'include'
    fetch(url, { credentials: 'include' })
      .then((r) => r.blob())
      .then((b) => window.open(URL.createObjectURL(b), "_blank"));
  };

  const doRientro = async (e) => {
    e.preventDefault();
    const km = +e.target.km.value;
    const autoFat = e.target.auto_fattura?.checked !== false; // default ON
    const inviaSdi = !!e.target.invia_sdi?.checked;
    const pecCli = !!e.target.pec_cliente?.checked;
    try {
      const r = await api.post(`/contracts/${showRientro.id}/rientro?km_rientro=${km}&auto_fattura=${autoFat}&invia_sdi=${inviaSdi}&pec_cliente=${pecCli}`);
      const fInfo = r.data?.fattura;
      const pecInfo = r.data?.pec_cliente;
      if (fInfo?.created) {
        toast.success(`Rientro registrato · Fattura ${fInfo.numero} ${fInfo.stato === 'inviata' ? 'inviata a SDI ✓' : 'generata'}`);
      } else if (fInfo?.skipped) {
        toast.success(`Rientro registrato · Fattura ${fInfo.numero} già esistente`);
      } else if (autoFat && fInfo?.error) {
        toast.error("Rientro OK, ma errore fattura: " + fInfo.error);
      } else {
        toast.success("Rientro registrato");
      }
      if (pecInfo?.sent) {
        toast.success(`📧 PEC fattura inviata al cliente: ${pecInfo.destinatario}`);
      } else if (pecCli && pecInfo?.error) {
        toast.error("Errore invio PEC al cliente: " + pecInfo.error);
      }
    } catch (err) {
      toast.error("Errore rientro: " + (err?.response?.data?.detail || err.message));
    }
    setShowRientro(null); load();
  };

  const inviaFatturaPec = async (c) => {
    const proceed = window.confirm(`Inviare via PEC al cliente ${c.customer?.ragione_sociale || `${c.customer?.nome||''} ${c.customer?.cognome||''}`} la copia della fattura del contratto ${c.numero}?\n\nAllegati: PDF riassuntivo + XML FatturaPA.\nDestinatario: ${c.customer?.pec || c.customer?.email || '(da inserire)'}`);
    if (!proceed) return;
    try {
      const r = await api.post(`/contracts/${c.id}/fattura-pec-send`, {});
      toast.success(`📧 PEC fattura inviata a ${r.data?.log?.destinatario}`);
    } catch (e) {
      const det = e?.response?.data?.detail || e.message;
      toast.error("Errore invio PEC: " + det);
    }
  };

  const doProroga = async (e) => {
    e.preventDefault();
    const nuova = e.target.nuova.value;
    const costo = +e.target.costo.value || 0;
    await api.post(`/contracts/${showProroga.id}/proroga?nuova_data_fine=${nuova}&costo_aggiuntivo=${costo}`);
    toast.success("Proroga applicata"); setShowProroga(null); load();
  };

  const doSost = async (e) => {
    e.preventDefault();
    const cid = e.target.car.value;
    await api.post(`/contracts/${showSost.id}/sostituisci?nuovo_car_id=${cid}`);
    toast.success("Veicolo sostituito"); setShowSost(null); load();
  };

  const payStripe = async (c, tipo) => {
    try {
      const r = await api.post("/checkout/session", {
        tipo: `contract_${tipo}`, ref_id: c.id, origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) { toast.error("Errore Stripe: " + (e?.response?.data?.detail || e.message)); }
  };

  const whatsappShare = (c) => {
    const nome = c.customer?.ragione_sociale || `${c.customer?.nome || ''} ${c.customer?.cognome || ''}`;
    const veicolo = `${c.car?.marca || ''} ${c.car?.modello || ''} (${c.car?.targa || ''})`;
    const oc = c.ora_consegna ? ` h ${c.ora_consegna}` : '';
    const orc = c.ora_riconsegna ? ` h ${c.ora_riconsegna}` : '';
    const text = `📄 *Contratto FabriGroup ${c.numero}*\n\n👤 ${nome}\n🚗 ${veicolo}\n📅 Dal ${c.data_inizio}${oc} al ${c.data_fine}${orc}\n💶 Totale: €${c.costo_totale} (saldo €${c.saldo})`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  // === Dispatch contratto per firma a distanza (Email + WhatsApp + OTP) ===
  const [sendDispatch, setSendDispatch] = useState(null); // contratto selezionato per il dispatch modal
  const [dispatchChannels, setDispatchChannels] = useState({ email: true, whatsapp: true });
  const [dispatchBusy, setDispatchBusy] = useState(false);
  const [dispatchResult, setDispatchResult] = useState(null);

  const doDispatch = async () => {
    if (!sendDispatch) return;
    const channels = Object.entries(dispatchChannels).filter(([_, v]) => v).map(([k]) => k);
    if (channels.length === 0) { toast.error("Seleziona almeno un canale"); return; }
    setDispatchBusy(true);
    try {
      const r = await api.post(`/contracts/${sendDispatch.id}/send-for-signing`, { channels });
      setDispatchResult(r.data);
      const emRes = r.data?.channels?.email;
      const waRes = r.data?.channels?.whatsapp;
      if (emRes?.sent) toast.success(`📧 Email inviata a ${emRes.recipient}`);
      else if (emRes?.mailto) toast.info("Email pronta — clicca su 'Apri Email' per inviare manualmente");
      else if (emRes?.error) toast.error(`Email: ${emRes.error}`);
      if (waRes?.share_url) toast.success("WhatsApp pronto — clicca su 'Apri WhatsApp' per inviare");
    } catch (e) {
      toast.error("Errore: " + (e?.response?.data?.detail || e.message));
    } finally { setDispatchBusy(false); }
  };

  const generaFattura = async (c) => {
    const invia = window.confirm(`Generare la fattura elettronica per il contratto ${c.numero}?\n\nOK = genera XML + invia a SDI tramite Aruba\nAnnulla = solo genera XML (potrai inviare dopo dalla pagina Fatture)`);
    try {
      const r = await api.post(`/contracts/${c.id}/fattura-elettronica`, { invia: invia, aliquota_iva: 22 });
      const stato = r.data.stato === 'inviata' ? 'inviata a SDI ✓' : (r.data.stato === 'errore' ? 'generata MA errore invio' : 'generata');
      toast.success(`Fattura ${r.data.numero} ${stato}`);
    } catch (e) {
      toast.error("Errore fattura: " + (e?.response?.data?.detail || e.message));
    }
  };

  // === STRIPE CAUZIONE HOLD ===
  const cauzioneHold = async (c) => {
    const importo = c.deposito_cauzionale || +window.prompt("Importo cauzione da pre-autorizzare (€):", "1500") || 0;
    if (!importo || importo <= 0) return;
    const proceed = window.confirm(`Pre-autorizzare € ${importo} sulla carta del cliente?\n\nL'importo NON viene addebitato — resta solo "trattenuto" sulla carta fino al ritorno del veicolo.`);
    if (!proceed) return;
    try {
      const r = await api.post("/stripe/cauzione/hold", {
        contract_id: c.id, importo: +importo, return_url: window.location.origin + "/admin/contracts",
      });
      if (r.data.url) {
        window.open(r.data.url, "_blank");
        toast.success("Link pre-autorizzazione aperto. Far completare al cliente.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore cauzione");
    }
  };

  const cauzioneRelease = async (c) => {
    if (!window.confirm("Rilasciare la cauzione? La carta del cliente non verrà più addebitata.")) return;
    try {
      const r = await api.post(`/stripe/cauzione/${c.id}/release`);
      toast.success(r.data.message); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const cauzioneCapture = async (c) => {
    const v = window.prompt(`Importo da addebitare al cliente (max € ${c.cauzione_stripe?.amount || 0}):`, c.cauzione_stripe?.amount || "");
    if (!v) return;
    try {
      const r = await api.post(`/stripe/cauzione/${c.id}/capture`, null, { params: { importo: +v } });
      toast.success(`Addebitati € ${r.data.captured}`); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  // === FIRMA OTP A DISTANZA ===
  const openSign = (c) => {
    setShowSign(c);
    setSignChannel("whatsapp");
    setSignInit(null);
    setOtpInput("");
  };

  const initSign = async () => {
    setSignLoading(true);
    try {
      const r = await api.post(`/contracts/${showSign.id}/sign/init`, { channel: signChannel });
      setSignInit(r.data);
      toast.success("Codice OTP generato. Inviarlo al cliente.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore generazione OTP");
    } finally {
      setSignLoading(false);
    }
  };

  const verifySign = async () => {
    if (!otpInput || otpInput.length < 4) return toast.error("Inserisci il codice OTP comunicato dal cliente");
    setSignLoading(true);
    try {
      const r = await api.post(`/contracts/${showSign.id}/sign/verify`, {
        otp: otpInput.trim(),
        client_ip: "",          // sarà popolato dalla pagina pubblica
        user_agent: navigator.userAgent,
      });
      toast.success(r.data.message);
      setShowSign(null);
      setSignInit(null);
      setOtpInput("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OTP non valido");
    } finally {
      setSignLoading(false);
    }
  };

  // Genera URL pubblica firma + testo da inviare al cliente
  const signPublicUrl = (c, token) => `${window.location.origin}/sign/${c.id}?token=${token}`;
  const signMessage = (c, token) => {
    const veicolo = `${c.car?.marca || ''} ${c.car?.modello || ''} (${c.car?.targa || ''})`;
    const totale = c.costo_totale?.toFixed?.(2) || c.costo_totale;
    return `🔏 *Contratto FabriGroup ${c.numero} — Firma a distanza*\n\n` +
           `Gentile cliente,\nper firmare il contratto di noleggio del veicolo ${veicolo} per un totale di €${totale}, ` +
           `apri il link sottostante e inserisci il codice OTP a 6 cifre che ti comunicheremo:\n\n` +
           `${signPublicUrl(c, token)}\n\n` +
           `Il codice è valido 30 minuti.\nGrazie, FabriGroup Rent Manager.`;
  };

  const sendSignBy = (channel) => {
    if (!signInit) return;
    const msg = signMessage(showSign, signInit.sign_token);
    const url = signPublicUrl(showSign, signInit.sign_token);
    const tel = (showSign.customer?.telefono || "").replace(/[^0-9+]/g, "");
    const email = showSign.customer?.email || "";
    if (channel === "whatsapp") {
      window.open(`https://wa.me/${tel.replace(/^\+/, "")}?text=${encodeURIComponent(msg)}`, "_blank");
    } else if (channel === "sms") {
      window.open(`sms:${tel}?body=${encodeURIComponent(msg)}`, "_blank");
    } else if (channel === "email") {
      const subj = `Firma contratto FabriGroup ${showSign.numero}`;
      window.open(`mailto:${email}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(msg)}`, "_blank");
    } else if (channel === "copy") {
      navigator.clipboard.writeText(url);
      toast.success("Link copiato negli appunti");
    }
  };

  const startEdit = (c) => { setForm({ ...empty, ...c }); setEditId(c.id); setShowForm(true); };

  // === Quick add cliente direttamente dal form contratto ===
  const openQuickCust = () => {
    setQc(emptyQuickCustomer);
    setShowQuickCust(true);
  };
  const saveQuickCustomer = async (e) => {
    e.preventDefault();
    // validazione minima
    if (qc.tipo === "privato" && (!qc.nome || !qc.cognome)) {
      toast.error("Nome e Cognome obbligatori per cliente privato");
      return;
    }
    if (qc.tipo === "societa" && !qc.ragione_sociale) {
      toast.error("Ragione sociale obbligatoria per società");
      return;
    }
    if (!qc.codice_fiscale && !qc.piva) {
      toast.error("Inserisci almeno Codice Fiscale o P.IVA");
      return;
    }
    setQcSaving(true);
    try {
      // payload sicuro: email vuota → null (EmailStr accetta None)
      const payload = {
        ...qc,
        email: qc.email && qc.email.trim() ? qc.email.trim() : null,
      };
      const r = await api.post("/customers", payload);
      const newCust = r.data;
      // ricarico lista + auto-seleziono
      const cu = await api.get("/customers");
      setCustomers(cu.data);
      setForm((f) => ({ ...f, customer_id: newCust.id || newCust.customer_id }));
      setCustSearch(
        newCust.ragione_sociale ||
        `${newCust.nome || qc.nome} ${newCust.cognome || qc.cognome}`.trim()
      );
      setShowQuickCust(false);
      setCustOpen(false);
      toast.success("Cliente creato e selezionato. Salvato anche in anagrafica.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore creazione cliente");
    } finally {
      setQcSaving(false);
    }
  };

  // Cliente selezionato (display)
  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === form.customer_id),
    [customers, form.customer_id]
  );
  const customerDisplay = (c) => c?.ragione_sociale || `${c?.nome || ""} ${c?.cognome || ""}`.trim();
  const filteredCustomers = useMemo(() => {
    const s = (custSearch || "").toLowerCase().trim();
    return customers
      .filter((c) => !c.blacklist)
      .filter((c) => {
        if (!s) return true;
        return [c.nome, c.cognome, c.ragione_sociale, c.codice_fiscale, c.piva, c.email, c.telefono]
          .filter(Boolean).some((x) => String(x).toLowerCase().includes(s));
      })
      .slice(0, 30);
  }, [customers, custSearch]);

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">Contratti di Noleggio</h1>
          <p className="text-sm text-white/45 mt-1">{list.length} contratti · {list.filter(c=>c.stato==='attivo').length} attivi</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input data-testid="contract-search" placeholder="Cerca numero, cliente, targa…" value={search} onChange={(e) => setSearch(e.target.value)}
              className="bg-white/[0.03] border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none w-72" />
          </div>
          <button data-testid="contract-add" onClick={() => { setForm(empty); setEditId(null); setShowForm(true); }}
            className="btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2">
            <Plus size={14} /> Nuovo Contratto
          </button>
        </div>
      </div>

      <div className="glass rounded-2xl overflow-x-auto">
        <table className="w-full text-xs min-w-[1100px]">
          <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-white/40">
            <tr>
              <th className="text-left p-3">Contratto</th>
              <th className="text-left p-3">Cliente</th>
              <th className="text-left p-3">Veicolo</th>
              <th className="p-3">Periodo</th>
              <th className="p-3 text-right">Costo</th>
              <th className="p-3">Stato</th>
              <th className="p-3 text-center">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {filtered.map((c) => (
              <motion.tr key={c.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="hover:bg-white/[0.02]" data-testid={`contract-${c.numero}`}>
                <td className="p-3">
                  <div className="font-mono font-bold text-[11px]">{c.numero}</div>
                  <div className="text-[10px] text-white/40">op. {c.operatore_nome}</div>
                </td>
                <td className="p-3">
                  <div className="font-medium">{c.customer?.ragione_sociale || `${c.customer?.nome||''} ${c.customer?.cognome||''}`}</div>
                  <div className="text-[10px] text-white/40 font-mono">{c.customer?.codice_fiscale || c.customer?.piva || '—'}</div>
                </td>
                <td className="p-3">
                  <div className="font-medium">{c.car?.marca} {c.car?.modello}</div>
                  <div className="text-[10px] text-white/40 font-mono">{c.car?.targa}</div>
                </td>
                <td className="p-3 text-center font-mono text-[11px] whitespace-nowrap">
                  <div>{c.data_inizio?.slice(5)} → {c.data_fine?.slice(5)}</div>
                  {(c.ora_consegna || c.ora_riconsegna) && (
                    <div className="text-[9px] text-white/40 mt-0.5">
                      {c.ora_consegna || '—'} → {c.ora_riconsegna || '—'}
                    </div>
                  )}
                </td>
                <td className="p-3 text-right">
                  <div className="font-display font-extrabold neon-text">€{c.costo_totale?.toFixed(2)}</div>
                  <div className="text-[10px] text-white/40">saldo €{c.saldo?.toFixed(2)}</div>
                </td>
                <td className="p-3 text-center">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${STATO[c.stato]}`}>{c.stato}</span>
                </td>
                <td className="p-3">
                  <div className="flex items-center justify-center gap-1">
                    <button title="Stampa contratto" data-testid={`act-print-${c.numero}`} onClick={() => openPDF(c.id, 'contratto')}
                      className="p-1.5 rounded-lg bg-white/[0.04] hover:bg-[#ff0033]/15 text-white/60 hover:text-[#ff0033]"><Printer size={12} /></button>
                    <button title="Proforma" data-testid={`act-proforma-${c.numero}`} onClick={() => openPDF(c.id, 'proforma')}
                      className="p-1.5 rounded-lg bg-white/[0.04] hover:bg-[#ff0033]/15 text-white/60 hover:text-[#ff0033]"><FileText size={12} /></button>
                    <button title="C.A.R.G.O.S" data-testid={`act-cargos-${c.numero}`} onClick={() => openPDF(c.id, 'cargos')}
                      className="p-1.5 rounded-lg bg-white/[0.04] hover:bg-[#ff0033]/15 text-white/60 hover:text-[#ff0033]"><FileCheck size={12} /></button>
                    <button title="Rientro" data-testid={`act-rientro-${c.numero}`} onClick={() => setShowRientro(c)}
                      className="p-1.5 rounded-lg bg-white/[0.04] hover:bg-emerald-500/15 text-white/60 hover:text-emerald-300"><RotateCcw size={12} /></button>
                    <button title="Proroga" data-testid={`act-proroga-${c.numero}`} onClick={() => setShowProroga(c)}
                      className="p-1.5 rounded-lg bg-white/[0.04] hover:bg-amber-500/15 text-white/60 hover:text-amber-300"><RefreshCw size={12} /></button>
                    <button title="Sostituisci" data-testid={`act-sostituisci-${c.numero}`} onClick={() => setShowSost(c)}
                      className="p-1.5 rounded-lg bg-white/[0.04] hover:bg-blue-500/15 text-white/60 hover:text-blue-300"><Replace size={12} /></button>
                    <button title="Modifica" data-testid={`act-edit-${c.numero}`} onClick={() => startEdit(c)}
                      className="p-1.5 rounded-lg bg-white/[0.04] hover:bg-white/10 text-white/60 hover:text-white"><Pencil size={12} /></button>
                    <button title={c.stato_firma === 'firmato' ? "Firmato OTP" : "Firma OTP a distanza (mostra OTP)"} data-testid={`act-sign-${c.numero}`}
                      onClick={() => openSign(c)} disabled={c.stato_firma === 'firmato'}
                      className={`p-1.5 rounded-lg ${c.stato_firma === 'firmato' ? "bg-emerald-500/15 text-emerald-300 cursor-default" : "bg-white/[0.04] hover:bg-[#ff0033]/15 text-white/60 hover:text-[#ff0033]"}`}>
                      <ShieldCheck size={12} />
                    </button>
                    <button title="📤 Invia contratto al cliente (Email/WhatsApp) con OTP per firma da remoto" data-testid={`act-dispatch-${c.numero}`}
                      onClick={() => { setSendDispatch(c); setDispatchResult(null); setDispatchChannels({ email: true, whatsapp: true }); }}
                      disabled={c.stato_firma === 'firmato'}
                      className={`p-1.5 rounded-lg ${c.stato_firma === 'firmato' ? "bg-emerald-500/10 text-emerald-300/50 cursor-default" : "bg-blue-500/10 hover:bg-blue-500/25 text-blue-300"}`}>
                      <Send size={12} />
                    </button>
                    {c.saldo > 0 && (
                      <button title="Pagamento Stripe (saldo)" data-testid={`act-stripe-${c.numero}`} onClick={() => payStripe(c, 'saldo')}
                        className="p-1.5 rounded-lg bg-white/[0.04] hover:bg-violet-500/15 text-white/60 hover:text-violet-300"><CreditCard size={12} /></button>
                    )}
                    {/* Cauzione Stripe — 3 stati: nuova / pendente / completata / rilasciata */}
                    {!c.cauzione_stripe && (
                      <button title="Trattieni cauzione (Stripe hold)" data-testid={`act-cauz-hold-${c.numero}`} onClick={() => cauzioneHold(c)}
                        className="p-1.5 rounded-lg bg-white/[0.04] hover:bg-amber-500/15 text-white/60 hover:text-amber-300"><Lock size={12} /></button>
                    )}
                    {c.cauzione_stripe && c.cauzione_stripe.status !== 'released' && c.cauzione_stripe.status !== 'captured' && (
                      <>
                        <button title={`Cauzione € ${c.cauzione_stripe.amount} - Cattura (in caso di danni)`} data-testid={`act-cauz-cap-${c.numero}`} onClick={() => cauzioneCapture(c)}
                          className="p-1.5 rounded-lg bg-amber-500/15 text-amber-300 hover:bg-amber-500/25"><Banknote size={12} /></button>
                        <button title="Rilascia cauzione (fine noleggio senza danni)" data-testid={`act-cauz-rel-${c.numero}`} onClick={() => cauzioneRelease(c)}
                          className="p-1.5 rounded-lg bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"><Unlock size={12} /></button>
                      </>
                    )}
                    <button title="Condividi WhatsApp" data-testid={`act-wa-${c.numero}`} onClick={() => whatsappShare(c)}
                      className="p-1.5 rounded-lg bg-white/[0.04] hover:bg-emerald-500/15 text-white/60 hover:text-emerald-300"><MessageCircle size={12} /></button>
                    <button title="Genera Fattura Elettronica" data-testid={`act-fattura-${c.numero}`} onClick={() => generaFattura(c)}
                      className="p-1.5 rounded-lg bg-[#ff0033]/10 hover:bg-[#ff0033]/25 text-[#ff5577]"><ReceiptText size={12} /></button>
                    <button title="Invia fattura via PEC al cliente (PDF + XML)" data-testid={`act-fattura-pec-${c.numero}`} onClick={() => inviaFatturaPec(c)}
                      className="p-1.5 rounded-lg bg-fuchsia-500/10 hover:bg-fuchsia-500/25 text-fuchsia-300"><Mail size={12} /></button>
                    <button title="Elimina" data-testid={`act-delete-${c.numero}`} onClick={() => remove(c.id)}
                      className="p-1.5 rounded-lg bg-white/[0.04] hover:bg-[#ff0033]/15 text-white/60 hover:text-[#ff0033]"><Trash2 size={12} /></button>
                  </div>
                </td>
              </motion.tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="7" className="p-10 text-center text-white/40">Nessun contratto. Creane uno nuovo.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* CREATE/EDIT */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setShowForm(false)}>
          <motion.form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="w-full max-w-2xl glass-strong rounded-3xl p-7 max-h-[90vh] overflow-auto" data-testid="contract-form">
            <div className="font-display text-2xl font-bold mb-5">{editId ? "Modifica Contratto" : "Nuovo Contratto"}</div>

            {/* === AI DOCUMENT SCANNER === */}
            {!editId && (
              <div className="mb-5 p-4 rounded-2xl bg-gradient-to-br from-fuchsia-500/[0.08] to-[#ff0033]/[0.05] border border-fuchsia-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Brain size={16} className="text-fuchsia-300" />
                  <div className="font-display font-bold text-sm">🤖 Scansione AI documenti</div>
                  <span className="text-[9px] uppercase tracking-widest text-fuchsia-300/80 bg-fuchsia-500/15 px-1.5 py-0.5 rounded">auto-compila</span>
                </div>
                <p className="text-[11px] text-white/55 mb-3 leading-relaxed">
                  Carica <b>foto patente, CI, passaporto, libretto, voucher, contratti precedenti</b> o <b>PDF di qualsiasi tipo</b>. L'AI estrae i dati e compila il contratto in autonomia. Tutti i file vengono allegati all'anagrafica del contratto.
                </p>

                {/* Bottoni espliciti: foto vs PDF */}
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <label className={`flex items-center justify-center gap-2 cursor-pointer rounded-lg py-2.5 text-xs uppercase tracking-wider transition border ${scanning ? "border-fuchsia-500/40 bg-fuchsia-500/[0.06] text-fuchsia-300 cursor-wait" : "border-white/15 bg-white/[0.03] hover:border-fuchsia-500/40 hover:bg-fuchsia-500/[0.06] text-white/80"}`}
                    data-testid="ai-scan-photo-btn">
                    <input type="file" multiple accept="image/*" capture="environment"
                      className="hidden" onChange={(e) => onScanFiles(e.target.files)} disabled={scanning} />
                    <Camera size={14} /> 📷 Scatta foto / galleria
                  </label>
                  <label className={`flex items-center justify-center gap-2 cursor-pointer rounded-lg py-2.5 text-xs uppercase tracking-wider transition border ${scanning ? "border-fuchsia-500/40 bg-fuchsia-500/[0.06] text-fuchsia-300 cursor-wait" : "border-white/15 bg-white/[0.03] hover:border-fuchsia-500/40 hover:bg-fuchsia-500/[0.06] text-white/80"}`}
                    data-testid="ai-scan-pdf-btn">
                    <input type="file" multiple accept="application/pdf,.pdf"
                      className="hidden" onChange={(e) => onScanFiles(e.target.files)} disabled={scanning} />
                    <FileText size={14} /> 📄 Carica PDF
                  </label>
                </div>

                <label className={`block cursor-pointer rounded-xl border-2 border-dashed transition p-4 text-center ${scanning ? "border-fuchsia-500/40 bg-fuchsia-500/[0.06]" : "border-white/15 hover:border-fuchsia-500/40 hover:bg-fuchsia-500/[0.04]"}`}
                  data-testid="ai-scan-dropzone">
                  <input type="file" multiple accept="image/*,application/pdf,.pdf,.jpg,.jpeg,.png,.webp,.heic"
                    className="hidden" onChange={(e) => onScanFiles(e.target.files)}
                    data-testid="ai-scan-input" disabled={scanning} />
                  {scanning ? (
                    <div className="inline-flex items-center gap-2 text-fuchsia-300 text-sm">
                      <Loader2 size={16} className="animate-spin" /> Analisi AI in corso…
                    </div>
                  ) : (
                    <div>
                      <Upload size={22} className="mx-auto text-white/40 mb-1.5" />
                      <div className="text-xs text-white/70">…oppure trascina qui <b>più file insieme</b> (foto + PDF mescolati)</div>
                      <div className="text-[10px] text-white/35 mt-0.5">Max 10 file · 12MB l'uno · auto-OCR GPT-4o Vision</div>
                    </div>
                  )}
                </label>
                {scanResult && (
                  <div className="mt-3 p-3 rounded-lg bg-emerald-500/[0.05] border border-emerald-500/30">
                    <div className="text-[10px] uppercase font-bold text-emerald-300 mb-1.5">✓ Dati estratti automaticamente</div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                      {scanResult.persona?.nome && <div><span className="text-white/40">Nome:</span> <b>{scanResult.persona.nome} {scanResult.persona.cognome || ""}</b></div>}
                      {scanResult.persona?.codice_fiscale && <div><span className="text-white/40">CF:</span> <code>{scanResult.persona.codice_fiscale}</code></div>}
                      {scanResult.documento?.numero && <div><span className="text-white/40">Doc N°:</span> <code>{scanResult.documento.numero}</code></div>}
                      {scanResult.documento?.data_scadenza && <div><span className="text-white/40">Scadenza:</span> {scanResult.documento.data_scadenza}</div>}
                      {scanResult.veicolo?.targa && <div><span className="text-white/40">Targa:</span> <code>{scanResult.veicolo.targa}</code></div>}
                      {scanResult.bozza?.car_matched ? <div className="text-emerald-300">✓ Veicolo trovato in flotta</div>
                        : scanResult.veicolo?.targa && <div className="text-amber-300">⚠ Veicolo non in flotta</div>}
                      {scanResult.bozza?.customer_created ? <div className="text-fuchsia-300">✨ Cliente creato automaticamente da OCR</div>
                        : scanResult.bozza?.customer_matched ? <div className="text-emerald-300">✓ Cliente trovato in anagrafica</div>
                        : scanResult.persona?.codice_fiscale && <div className="text-amber-300">⚠ Cliente nuovo — usa "Nuovo" per crearlo manualmente</div>}
                    </div>
                  </div>
                )}
                {(form.pending_attachments || []).length > 0 && (
                  <div className="mt-3 space-y-1">
                    <div className="text-[10px] uppercase tracking-widest text-white/40">Allegati pronti per il salvataggio</div>
                    {form.pending_attachments.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 bg-white/[0.03] border border-white/10 rounded-md text-[11px]">
                        <Paperclip size={11} className="text-fuchsia-300" />
                        <span className="flex-1 truncate">{a.filename}</span>
                        <span className="text-[9px] text-white/40">{Math.round((a.size || 0) / 1024)}kb</span>
                        <button type="button" onClick={() => removePendingAtt(i)} className="text-rose-300 hover:text-rose-200"><X size={11} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-white/40">Cliente</label>
                <div className="mt-1 flex gap-2 items-stretch">
                  {/* Combobox searchable */}
                  <div className="relative flex-1">
                    <button type="button"
                      data-testid="contract-cust-combobox"
                      onClick={() => setCustOpen((v) => !v)}
                      className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm text-left flex items-center justify-between hover:border-white/25 focus:border-[#ff0033]/60 focus:outline-none">
                      <span className={selectedCustomer ? "" : "text-white/40"}>
                        {selectedCustomer
                          ? `${customerDisplay(selectedCustomer)} · ${selectedCustomer.codice_fiscale || selectedCustomer.piva || selectedCustomer.email || ""}`
                          : "Seleziona o cerca cliente…"}
                      </span>
                      <ChevronDown size={14} className={`text-white/40 transition-transform ${custOpen ? "rotate-180" : ""}`} />
                    </button>
                    {custOpen && (
                      <div className="absolute z-20 mt-1 left-0 right-0 bg-[#0a0a0a] border border-white/10 rounded-lg shadow-2xl overflow-hidden">
                        <div className="p-2 border-b border-white/5">
                          <input autoFocus value={custSearch} onChange={(e) => setCustSearch(e.target.value)}
                            data-testid="contract-cust-search"
                            placeholder="Cerca per nome, CF, P.IVA, email…"
                            className="w-full bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
                        </div>
                        <div className="max-h-60 overflow-auto">
                          {filteredCustomers.length === 0 && (
                            <div className="p-4 text-center text-xs text-white/40">Nessun cliente trovato</div>
                          )}
                          {filteredCustomers.map((c) => (
                            <button key={c.id} type="button"
                              data-testid={`contract-cust-opt-${c.id}`}
                              onClick={() => { setForm({ ...form, customer_id: c.id }); setCustOpen(false); setCustSearch(""); }}
                              className={`w-full text-left px-3 py-2 text-xs hover:bg-[#ff0033]/[0.08] flex items-center justify-between gap-2 border-b border-white/5 last:border-0 ${form.customer_id === c.id ? "bg-[#ff0033]/[0.12]" : ""}`}>
                              <div className="min-w-0">
                                <div className="font-medium truncate">{customerDisplay(c)}</div>
                                <div className="text-[10px] text-white/40 font-mono truncate">{c.codice_fiscale || c.piva || "—"} · {c.email || c.telefono || ""}</div>
                              </div>
                              {form.customer_id === c.id && <Check size={12} className="text-[#ff0033] shrink-0" />}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Nuovo cliente quick */}
                  <button type="button" onClick={openQuickCust}
                    data-testid="contract-cust-new"
                    className="px-4 py-2 rounded-lg bg-[#ff0033]/15 border border-[#ff0033]/30 text-[#ff5577] hover:bg-[#ff0033]/25 text-[11px] uppercase tracking-widest inline-flex items-center gap-1.5 whitespace-nowrap">
                    <UserPlus size={13} /> Nuovo
                  </button>
                </div>
                {!form.customer_id && (
                  <div className="mt-1 text-[10px] text-white/30">
                    Tip: puoi creare al volo un nuovo cliente con <b className="text-[#ff5577]">Nuovo</b> — verrà salvato anche in Anagrafiche Clienti.
                  </div>
                )}
                {/* hidden required input per validazione browser */}
                <input type="hidden" required value={form.customer_id} readOnly />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-white/40">Veicolo</label>
                <select required value={form.car_id} onChange={(e) => setForm({ ...form, car_id: e.target.value })}
                  className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="">Seleziona…</option>
                  {cars.filter((c) => c.stato === "disponibile" || c.id === form.car_id).map((c) => (
                    <option key={c.id} value={c.id}>{c.marca} {c.modello} · {c.targa} · €{c.prezzo_giornaliero}/g</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Data inizio</label>
                <input required type="date" value={form.data_inizio} onChange={(e) => setForm({ ...form, data_inizio: e.target.value })}
                  data-testid="ct-data-inizio"
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Ora consegna</label>
                <input type="time" value={form.ora_consegna} onChange={(e) => setForm({ ...form, ora_consegna: e.target.value })}
                  data-testid="ct-ora-consegna"
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Data fine</label>
                <input required type="date" value={form.data_fine} onChange={(e) => setForm({ ...form, data_fine: e.target.value })}
                  data-testid="ct-data-fine"
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Ora riconsegna</label>
                <input type="time" value={form.ora_riconsegna} onChange={(e) => setForm({ ...form, ora_riconsegna: e.target.value })}
                  data-testid="ct-ora-riconsegna"
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Km uscita</label>
                <input type="number" value={form.km_uscita} onChange={(e) => setForm({ ...form, km_uscita: e.target.value })}
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Lingua stampa</label>
                <select value={form.lingua} onChange={(e) => setForm({ ...form, lingua: e.target.value })}
                  className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="it">Italiano</option><option value="en">English</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-white/40">Luogo ritiro</label>
                <AddressAutocomplete
                  testid="ct-luogo-ritiro"
                  value={form.luogo_ritiro}
                  onChange={(v) => setForm({ ...form, luogo_ritiro: v })}
                  onSelect={(p) => setForm({ ...form, luogo_ritiro: p.display_name || p.indirizzo })}
                  placeholder="Es. Via Selice 22, Imola (BO)"
                />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-white/40">Luogo riconsegna</label>
                <AddressAutocomplete
                  testid="ct-luogo-riconsegna"
                  value={form.luogo_riconsegna}
                  onChange={(v) => setForm({ ...form, luogo_riconsegna: v })}
                  onSelect={(p) => setForm({ ...form, luogo_riconsegna: p.display_name || p.indirizzo })}
                  placeholder="Lascia vuoto se uguale al ritiro"
                />
              </div>
              <div className="col-span-2 p-3 rounded-xl bg-gradient-to-br from-amber-500/[0.07] to-emerald-500/[0.05] border border-amber-500/30">
                <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-2 flex items-center gap-1.5">
                  <Banknote size={12} /> Tariffa modificabile
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-[9px] uppercase tracking-widest text-white/40">€/giorno (override)</label>
                    <input type="number" step="0.5"
                      value={form.prezzo_giornaliero_override == null ? "" : form.prezzo_giornaliero_override}
                      onChange={(e) => setForm({ ...form, prezzo_giornaliero_override: e.target.value === "" ? null : e.target.value, _auto_total: true })}
                      placeholder={(() => { const c = cars.find((x) => x.id === form.car_id); return c ? `${c.prezzo_giornaliero}` : "—"; })()}
                      data-testid="contract-price-day-override"
                      className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono text-right" />
                    <div className="text-[9px] text-white/40 mt-0.5">Lascia vuoto per usare la tariffa veicolo</div>
                  </div>
                  <div>
                    <label className="text-[9px] uppercase tracking-widest text-white/40">Giorni</label>
                    <input type="text" disabled
                      value={(() => {
                        if (!form.data_inizio || !form.data_fine) return "—";
                        const s = new Date(form.data_inizio), e = new Date(form.data_fine);
                        return Math.max(1, Math.round((e - s) / 86400000));
                      })()}
                      className="mt-1 w-full bg-white/[0.02] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono text-right text-white/60" />
                  </div>
                  <div>
                    <label className="text-[9px] uppercase tracking-widest text-emerald-300">Costo Totale € *</label>
                    <input required type="number" step="0.01"
                      value={form.costo_totale}
                      onChange={(e) => setForm({ ...form, costo_totale: e.target.value, _auto_total: false })}
                      data-testid="contract-total"
                      className="mt-1 w-full bg-emerald-500/[0.05] border border-emerald-500/30 rounded-lg px-3 py-2 text-sm font-mono font-bold text-right text-emerald-200" />
                  </div>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Acconto €</label>
                <input type="number" step="0.01" value={form.acconto} onChange={(e) => setForm({ ...form, acconto: e.target.value })}
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-white/40">Metodo pagamento</label>
                <select value={form.metodo_pagamento} onChange={(e) => setForm({ ...form, metodo_pagamento: e.target.value })}
                  className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  {["contanti", "pos", "sumup", "bonifico", "stripe", "paypal", "consegna"].map((m) => (
                    <option key={m} value={m}>
                      {m === "consegna" ? "Pagamento alla consegna" :
                       m === "sumup" ? "SumUp POS" :
                       m === "pos" ? "POS terminale" :
                       m.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-2 p-3 rounded-xl bg-white/[0.02] border border-white/5 text-xs">
                <div className="text-white/40 uppercase tracking-widest text-[10px] mb-1">Riepilogo</div>
                <div className="flex justify-between"><span>Totale</span><b>€ {(+form.costo_totale).toFixed(2)}</b></div>
                <div className="flex justify-between"><span>Acconto</span><b>€ {(+form.acconto).toFixed(2)}</b></div>
                <div className="flex justify-between text-[#ff0033]"><span>Saldo da incassare</span><b>€ {Math.max(0, +form.costo_totale - +form.acconto).toFixed(2)}</b></div>
              </div>

              {/* === Franchigie modificabili per singolo contratto === */}
              <div className="col-span-2 p-4 rounded-xl bg-[#ff0033]/[0.04] border border-[#ff0033]/15">
                <div className="text-[10px] uppercase tracking-widest text-[#ff5577] mb-2 inline-flex items-center gap-2">
                  <ShieldCheck size={11} /> Franchigie & Deposito (override · lascia vuoto per usare default)
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  <div>
                    <label className="text-[9px] uppercase tracking-widest text-white/40">RCA €</label>
                    <input type="number" data-testid="ct-fran-rca" value={form.franchigia_rca ?? ""} onChange={(e) => setForm({ ...form, franchigia_rca: e.target.value === "" ? null : e.target.value })}
                      className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-2 py-1.5 text-xs focus:border-[#ff0033]/60 focus:outline-none" placeholder="default" />
                  </div>
                  <div>
                    <label className="text-[9px] uppercase tracking-widest text-white/40">Kasko €</label>
                    <input type="number" data-testid="ct-fran-kasko" value={form.franchigia_kasko ?? ""} onChange={(e) => setForm({ ...form, franchigia_kasko: e.target.value === "" ? null : e.target.value })}
                      className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-2 py-1.5 text-xs focus:border-[#ff0033]/60 focus:outline-none" placeholder="default" />
                  </div>
                  <div>
                    <label className="text-[9px] uppercase tracking-widest text-white/40">Furto €</label>
                    <input type="number" data-testid="ct-fran-furto" value={form.franchigia_furto ?? ""} onChange={(e) => setForm({ ...form, franchigia_furto: e.target.value === "" ? null : e.target.value })}
                      className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-2 py-1.5 text-xs focus:border-[#ff0033]/60 focus:outline-none" placeholder="default" />
                  </div>
                  <div>
                    <label className="text-[9px] uppercase tracking-widest text-white/40">Incendio €</label>
                    <input type="number" data-testid="ct-fran-incendio" value={form.franchigia_incendio ?? ""} onChange={(e) => setForm({ ...form, franchigia_incendio: e.target.value === "" ? null : e.target.value })}
                      className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-2 py-1.5 text-xs focus:border-[#ff0033]/60 focus:outline-none" placeholder="default" />
                  </div>
                  <div>
                    <label className="text-[9px] uppercase tracking-widest text-white/40">Cauzione €</label>
                    <input type="number" data-testid="ct-deposito" value={form.deposito_cauzionale ?? 0} onChange={(e) => setForm({ ...form, deposito_cauzionale: e.target.value })}
                      className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-2 py-1.5 text-xs focus:border-[#ff0033]/60 focus:outline-none" />
                  </div>
                </div>
                <div className="text-[10px] text-white/30 mt-2">
                  Valori vuoti = applica le franchigie aziendali di default impostate in Settings.
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6 justify-end">
              <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 rounded-full text-sm bg-white/[0.04] border border-white/10">Annulla</button>
              <button data-testid="contract-form-submit" type="submit" className="btn-neon px-6 py-2.5 rounded-full text-sm">{editId ? "Salva" : "Crea Contratto"}</button>
            </div>
          </motion.form>
        </div>
      )}

      {/* OCR Capture per Quick-Add cliente */}
      {showOcrCapture && (
        <DocumentCapture
          onClose={() => setShowOcrCapture(false)}
          onOcrResult={applyOcrToQc}
        />
      )}

      {/* QUICK ADD CLIENTE — direttamente dal contratto */}
      <AnimatePresence>
      {showQuickCust && (
        <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 md:p-6"
             onClick={() => !qcSaving && setShowQuickCust(false)}>
          <motion.form onClick={(e) => e.stopPropagation()} onSubmit={saveQuickCustomer}
            initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
            data-testid="quick-cust-form"
            className="w-full max-w-3xl bg-[#0a0a0a] border border-white/10 rounded-3xl overflow-hidden max-h-[92vh] flex flex-col shadow-[0_0_120px_-20px_#ff0033]">
            <div className="flex items-center justify-between px-7 py-5 border-b border-white/5 bg-gradient-to-r from-[#ff0033]/[0.08] via-transparent to-transparent">
              <div>
                <div className="font-display text-xl font-bold tracking-tight inline-flex items-center gap-2">
                  <UserPlus size={18} className="text-[#ff0033]" /> Nuovo Cliente — Anagrafica veloce
                </div>
                <div className="text-[11px] text-white/40 mt-1">Compila i dati essenziali. Il cliente sarà salvato anche in <b>Anagrafiche Clienti</b> e auto-selezionato nel contratto.</div>
              </div>
              <button type="button" onClick={() => setShowQuickCust(false)} disabled={qcSaving}
                data-testid="quick-cust-close"
                className="p-2 rounded-full hover:bg-white/10 disabled:opacity-40"><X size={16} /></button>
            </div>

            <div className="flex-1 overflow-auto p-6 md:p-7 space-y-5">
              {/* OCR Documento — auto-fill */}
              <div className="rounded-xl border border-dashed border-[#ff0033]/30 bg-[#ff0033]/[0.04] p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <Camera size={18} className="text-[#ff5577] shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-bold">Scatta foto del documento</div>
                    <div className="text-[10px] text-white/45">L'AI legge nome, cognome, CF, indirizzo, patente e compila automaticamente i campi sottostanti</div>
                  </div>
                </div>
                <button type="button" onClick={() => setShowOcrCapture(true)} data-testid="qc-ocr-btn"
                  className="px-4 py-2 rounded-full text-xs uppercase tracking-widest bg-[#ff0033]/15 border border-[#ff0033]/40 text-[#ff5577] hover:bg-[#ff0033]/25 inline-flex items-center gap-1.5">
                  <Camera size={12} /> Scansiona
                </button>
              </div>

              {/* Tipo */}
              <div className="flex gap-2">
                {[
                  { v: "privato", label: "Privato" },
                  { v: "societa", label: "Azienda / P.IVA" },
                ].map((t) => (
                  <button key={t.v} type="button"
                    data-testid={`qc-tipo-${t.v}`}
                    onClick={() => setQc({ ...qc, tipo: t.v })}
                    className={`px-5 py-2 rounded-full text-xs uppercase tracking-widest border transition-all ${qc.tipo === t.v ? "border-[#ff0033] bg-[#ff0033]/10 text-white" : "border-white/10 bg-white/[0.02] text-white/55 hover:border-white/25"}`}>
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Anagrafica */}
              <div className="rounded-xl bg-white/[0.02] border border-white/5 p-4">
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-3">Anagrafica</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {qc.tipo === "privato" ? (
                    <>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-white/40">Nome *</label>
                        <input data-testid="qc-nome" value={qc.nome} onChange={(e) => setQc({ ...qc, nome: e.target.value })} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-white/40">Cognome *</label>
                        <input data-testid="qc-cognome" value={qc.cognome} onChange={(e) => setQc({ ...qc, cognome: e.target.value })} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-white/40">Codice Fiscale *</label>
                        <input data-testid="qc-cf" value={qc.codice_fiscale} onChange={(e) => setQc({ ...qc, codice_fiscale: e.target.value.toUpperCase() })} maxLength={16} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm uppercase font-mono focus:border-[#ff0033]/60 focus:outline-none" />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="md:col-span-2">
                        <label className="text-[10px] uppercase tracking-widest text-white/40">Ragione Sociale *</label>
                        <input data-testid="qc-rs" value={qc.ragione_sociale} onChange={(e) => setQc({ ...qc, ragione_sociale: e.target.value })} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-white/40">P.IVA *</label>
                        <input data-testid="qc-piva" value={qc.piva} onChange={(e) => setQc({ ...qc, piva: e.target.value })} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono focus:border-[#ff0033]/60 focus:outline-none" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-[10px] uppercase tracking-widest text-white/40">Codice Fiscale azienda</label>
                        <input value={qc.codice_fiscale} onChange={(e) => setQc({ ...qc, codice_fiscale: e.target.value.toUpperCase() })} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm uppercase font-mono focus:border-[#ff0033]/60 focus:outline-none" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-white/40">Rappresentante</label>
                        <input value={qc.nome} onChange={(e) => setQc({ ...qc, nome: e.target.value })} placeholder="Nome legale rappr." className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
                      </div>
                    </>
                  )}
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/40">Email</label>
                    <input data-testid="qc-email" type="email" value={qc.email} onChange={(e) => setQc({ ...qc, email: e.target.value })} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/40">Telefono</label>
                    <input data-testid="qc-tel" value={qc.telefono} onChange={(e) => setQc({ ...qc, telefono: e.target.value })} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/40">Rischio</label>
                    <select value={qc.rischio} onChange={(e) => setQc({ ...qc, rischio: e.target.value })} className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none">
                      <option value="basso">Basso</option>
                      <option value="medio">Medio</option>
                      <option value="alto">Alto</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Residenza con autocomplete globale + mappa */}
              <div className="rounded-xl bg-white/[0.02] border border-white/5 p-4">
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-3 inline-flex items-center gap-2">
                  Residenza / Sede <span className="text-[#ff5577] text-[9px] normal-case tracking-normal">· Autocomplete globale</span>
                </div>
                <AddressAutocomplete
                  testid="qc-addr"
                  value={qc.indirizzo}
                  onChange={(v) => setQc({ ...qc, indirizzo: v })}
                  onSelect={(p) => setQc({
                    ...qc,
                    indirizzo: p.indirizzo,
                    citta: p.citta || qc.citta,
                    cap: p.cap || qc.cap,
                    provincia: p.provincia || qc.provincia,
                    nazione: p.nazione || qc.nazione,
                    lat: p.lat, lng: p.lng,
                  })}
                  placeholder="Inizia a scrivere la via, città o CAP…"
                />
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/40">Città</label>
                    <input value={qc.citta} onChange={(e) => setQc({ ...qc, citta: e.target.value })} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/40">CAP</label>
                    <input value={qc.cap} onChange={(e) => setQc({ ...qc, cap: e.target.value })} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/40">Prov.</label>
                    <input maxLength={4} value={qc.provincia} onChange={(e) => setQc({ ...qc, provincia: e.target.value.toUpperCase() })} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm uppercase focus:border-[#ff0033]/60 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/40">Nazione</label>
                    <input value={qc.nazione} onChange={(e) => setQc({ ...qc, nazione: e.target.value })} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
                  </div>
                </div>
                {(qc.lat && qc.lng) && (
                  <div className="mt-3">
                    <LocationMap
                      lat={qc.lat} lng={qc.lng}
                      label={qc.indirizzo + (qc.citta ? `, ${qc.citta}` : "")}
                      height={180}
                      testid="qc-map"
                    />
                  </div>
                )}
              </div>

              {/* Patente */}
              <div className="rounded-xl bg-white/[0.02] border border-white/5 p-4">
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-3">Patente di guida</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/40">N° Patente</label>
                    <input data-testid="qc-pat-num" value={qc.patente_numero} onChange={(e) => setQc({ ...qc, patente_numero: e.target.value.toUpperCase() })} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm uppercase font-mono focus:border-[#ff0033]/60 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/40">Categoria</label>
                    <select value={qc.patente_categoria} onChange={(e) => setQc({ ...qc, patente_categoria: e.target.value })} className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none">
                      {["A", "A1", "A2", "B", "B1", "BE", "C", "CE", "D", "DE"].map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/40">Emissione</label>
                    <input type="date" value={qc.patente_emissione} onChange={(e) => setQc({ ...qc, patente_emissione: e.target.value })} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/40">Scadenza</label>
                    <input data-testid="qc-pat-scad" type="date" value={qc.patente_scadenza} onChange={(e) => setQc({ ...qc, patente_scadenza: e.target.value })} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase tracking-widest text-white/40">Rilasciata da (Comune / MCTC)</label>
                    <input value={qc.patente_comune} onChange={(e) => setQc({ ...qc, patente_comune: e.target.value })} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
                  </div>
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Note</label>
                <textarea rows={2} value={qc.note} onChange={(e) => setQc({ ...qc, note: e.target.value })} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none" placeholder="Note rapide…" />
              </div>

              <div className="text-[11px] text-white/45 inline-flex items-center gap-2">
                <FileText size={12} className="text-[#ff5577]" />
                Documenti completi (CI, allegati upload, conducenti aggiuntivi) modificabili dopo dalla sezione <b className="text-white/70">Clienti</b>.
              </div>
            </div>

            <div className="flex gap-3 px-7 py-4 border-t border-white/5 justify-end bg-black/40">
              <button type="button" onClick={() => setShowQuickCust(false)} disabled={qcSaving}
                className="px-5 py-2.5 rounded-full text-sm bg-white/[0.04] border border-white/10 hover:bg-white/10 disabled:opacity-50">Annulla</button>
              <button data-testid="qc-submit" type="submit" disabled={qcSaving}
                className="btn-neon px-6 py-2.5 rounded-full text-sm disabled:opacity-50 inline-flex items-center gap-2">
                {qcSaving ? "Salvataggio…" : (<><UserPlus size={14} /> Salva & Seleziona</>)}
              </button>
            </div>
          </motion.form>
        </div>
      )}
      </AnimatePresence>

      {/* === FIRMA OTP A DISTANZA === */}
      <AnimatePresence>
      {showSign && (
        <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 md:p-6"
             onClick={() => !signLoading && setShowSign(null)}>
          <motion.div onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
            className="w-full max-w-2xl bg-[#0a0a0a] border border-white/10 rounded-3xl overflow-hidden shadow-[0_0_120px_-20px_#ff0033]"
            data-testid="sign-modal">
            <div className="flex items-center justify-between px-7 py-5 border-b border-white/5 bg-gradient-to-r from-[#ff0033]/[0.08] via-transparent to-transparent">
              <div>
                <div className="font-display text-xl font-bold tracking-tight inline-flex items-center gap-2">
                  <ShieldCheck size={18} className="text-[#ff0033]" /> Firma Elettronica a Distanza
                </div>
                <div className="text-[11px] text-white/40 mt-1">
                  Contratto <b>{showSign.numero}</b> · {showSign.customer?.ragione_sociale || `${showSign.customer?.nome || ""} ${showSign.customer?.cognome || ""}`}
                </div>
              </div>
              <button onClick={() => setShowSign(null)} disabled={signLoading} className="p-2 rounded-full hover:bg-white/10 disabled:opacity-40"><X size={16} /></button>
            </div>

            <div className="p-6 md:p-7 space-y-5">
              {!signInit ? (
                <>
                  <div className="text-sm text-white/70">
                    Genera un codice OTP a 6 cifre e invialo al cliente tramite il canale preferito. Il cliente lo inserirà nella pagina di firma per concludere il contratto con valore legale (art. 20 CAD, eIDAS).
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { v: "whatsapp", label: "WhatsApp", icon: MessageCircle, hint: "Consigliato" },
                      { v: "sms", label: "SMS", icon: Phone, hint: "Sempre disponibile" },
                      { v: "email", label: "Email", icon: Mail, hint: "Anche allegato" },
                    ].map((ch) => {
                      const Ico = ch.icon;
                      const active = signChannel === ch.v;
                      return (
                        <button key={ch.v} onClick={() => setSignChannel(ch.v)}
                          data-testid={`sign-ch-${ch.v}`}
                          className={`p-4 rounded-xl border transition-all text-left ${active ? "border-[#ff0033] bg-[#ff0033]/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
                          <Ico size={18} className={active ? "text-[#ff5577]" : "text-white/60"} />
                          <div className="font-display font-bold text-sm mt-2">{ch.label}</div>
                          <div className="text-[10px] text-white/40">{ch.hint}</div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-[11px] text-white/45">
                    Consiglio: <b className="text-white/70">WhatsApp</b> è la scelta più rapida e tracciabile in Italia. Il messaggio includerà il link alla pagina firma + istruzioni.
                  </div>
                  <button onClick={initSign} disabled={signLoading}
                    data-testid="sign-init-btn"
                    className="btn-neon w-full py-3 rounded-full text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2">
                    <ShieldCheck size={14} /> {signLoading ? "Generazione…" : "Genera OTP & Invia"}
                  </button>
                </>
              ) : (
                <>
                  <div className="rounded-xl bg-[#ff0033]/[0.08] border border-[#ff0033]/30 p-5 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-[#ff5577] mb-2">Codice OTP per il cliente</div>
                    <div className="font-mono text-4xl font-extrabold tracking-[0.4em] text-white" data-testid="sign-otp-display">{signInit.otp}</div>
                    <div className="text-[10px] text-white/40 mt-2">Valido 30 minuti. Comunicalo al cliente tramite il canale scelto.</div>
                  </div>

                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Invia link firma al cliente</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <button onClick={() => sendSignBy("whatsapp")} data-testid="send-whatsapp"
                        className="p-3 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 inline-flex flex-col items-center gap-1">
                        <MessageCircle size={16} /><span className="text-[11px] uppercase tracking-wider">WhatsApp</span>
                      </button>
                      <button onClick={() => sendSignBy("sms")} data-testid="send-sms"
                        className="p-3 rounded-xl bg-blue-500/15 border border-blue-500/30 text-blue-300 hover:bg-blue-500/25 inline-flex flex-col items-center gap-1">
                        <Phone size={16} /><span className="text-[11px] uppercase tracking-wider">SMS</span>
                      </button>
                      <button onClick={() => sendSignBy("email")} data-testid="send-email"
                        className="p-3 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-300 hover:bg-amber-500/25 inline-flex flex-col items-center gap-1">
                        <Mail size={16} /><span className="text-[11px] uppercase tracking-wider">Email</span>
                      </button>
                      <button onClick={() => sendSignBy("copy")} data-testid="send-copy"
                        className="p-3 rounded-xl bg-white/[0.04] border border-white/15 text-white/70 hover:bg-white/10 inline-flex flex-col items-center gap-1">
                        <Copy size={16} /><span className="text-[11px] uppercase tracking-wider">Copia Link</span>
                      </button>
                    </div>
                  </div>

                  <div className="border-t border-white/5 pt-5">
                    <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Verifica OTP inserito dal cliente</div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={otpInput}
                        onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        data-testid="sign-otp-input"
                        maxLength={6}
                        placeholder="• • • • • •"
                        className="flex-1 bg-white/[0.03] border border-white/10 rounded-lg px-4 py-3 font-mono text-center text-2xl tracking-[0.4em] focus:border-[#ff0033]/60 focus:outline-none"
                      />
                      <button onClick={verifySign} disabled={signLoading || otpInput.length < 4}
                        data-testid="sign-verify-btn"
                        className="btn-neon px-6 py-3 rounded-lg text-sm disabled:opacity-50 inline-flex items-center gap-2">
                        <ShieldCheck size={14} /> Firma
                      </button>
                    </div>
                    <div className="text-[10px] text-white/30 mt-2">
                      Il cliente può anche firmare autonomamente dalla pagina pubblica aperta dal link. Quando il cliente firma, lo stato del contratto si aggiornerà automaticamente.
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
      </AnimatePresence>

      {/* RIENTRO */}
      {showRientro && (
        <Modal title="Registra Rientro" onClose={() => setShowRientro(null)}>
          <form onSubmit={doRientro} className="space-y-3">
            <div className="text-sm text-white/60">Contratto <b>{showRientro.numero}</b> · km uscita {showRientro.km_uscita}</div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40">Km al rientro</label>
              <input name="km" required type="number" min={showRientro.km_uscita || 0}
                data-testid="rientro-km"
                className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="p-3 rounded-lg bg-fuchsia-500/[0.06] border border-fuchsia-500/30 space-y-2">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" name="auto_fattura" defaultChecked data-testid="rientro-auto-fattura" />
                <span><b>Genera fattura elettronica</b> automaticamente alla chiusura <span className="text-fuchsia-300">(Cargos-style)</span></span>
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer text-white/70 pl-6">
                <input type="checkbox" name="invia_sdi" data-testid="rientro-invia-sdi" />
                <span>Invia subito a SDI via Aruba (XML pronto, anche senza spunta)</span>
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer text-white/70 pl-6">
                <input type="checkbox" name="pec_cliente" data-testid="rientro-pec-cliente" />
                <span>📧 Invia la fattura via <b>PEC al cliente</b> (PDF + XML in allegato)</span>
              </label>
            </div>
            <button className="btn-neon w-full py-2.5 rounded-full text-sm" data-testid="rientro-submit">Conferma Rientro</button>
          </form>
        </Modal>
      )}

      {/* PROROGA */}
      {showProroga && (
        <Modal title="Proroga Contratto" onClose={() => setShowProroga(null)}>
          <form onSubmit={doProroga} className="space-y-3">
            <div className="text-sm text-white/60">Contratto <b>{showProroga.numero}</b> · scadenza attuale {showProroga.data_fine}</div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40">Nuova data fine</label>
              <input name="nuova" required type="date" min={showProroga.data_fine}
                className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40">Costo aggiuntivo €</label>
              <input name="costo" type="number" step="0.01" defaultValue={0}
                className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
            </div>
            <button className="btn-neon w-full py-2.5 rounded-full text-sm">Applica Proroga</button>
          </form>
        </Modal>
      )}

      {/* SOSTITUZIONE */}
      {showSost && (
        <Modal title="Sostituisci Veicolo" onClose={() => setShowSost(null)}>
          <form onSubmit={doSost} className="space-y-3">
            <div className="text-sm text-white/60">Contratto <b>{showSost.numero}</b> · attuale {showSost.car?.marca} {showSost.car?.modello}</div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40">Nuovo veicolo</label>
              <select name="car" required
                className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
                <option value="">Seleziona…</option>
                {cars.filter((c) => c.stato === "disponibile").map((c) => (
                  <option key={c.id} value={c.id}>{c.marca} {c.modello} · {c.targa}</option>
                ))}
              </select>
            </div>
            <button className="btn-neon w-full py-2.5 rounded-full text-sm">Sostituisci</button>
          </form>
        </Modal>
      )}

      {/* === DISPATCH CONTRATTO (Email + WhatsApp + OTP firma) === */}
      {sendDispatch && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6" onClick={() => !dispatchBusy && setSendDispatch(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl glass-strong rounded-3xl p-6 max-h-[92vh] overflow-auto" data-testid="dispatch-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-display text-2xl font-bold flex items-center gap-2"><Send size={20} className="text-blue-300" /> Invia contratto per firma</div>
                <div className="text-xs text-white/45 mt-1">
                  Contratto <span className="font-mono text-white/80">{sendDispatch.numero}</span> ·
                  Cliente <b>{sendDispatch.customer?.ragione_sociale || `${sendDispatch.customer?.nome||''} ${sendDispatch.customer?.cognome||''}`}</b>
                </div>
              </div>
              <button onClick={() => !dispatchBusy && setSendDispatch(null)} className="text-white/40 hover:text-white">✕</button>
            </div>

            {!dispatchResult ? (
              <>
                <div className="p-3 rounded-lg bg-blue-500/[0.06] border border-blue-500/30 text-xs text-blue-200 mb-4">
                  Il sistema genererà un <b>codice OTP a 6 cifre</b> e invierà al cliente il link per firmare il contratto online dal cellulare.
                  Validità OTP: 30 minuti. Niente più cartaceo!
                </div>

                <div className="space-y-3 mb-4">
                  <label className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/10 cursor-pointer hover:border-blue-500/40">
                    <input type="checkbox" data-testid="dispatch-email-toggle" checked={dispatchChannels.email}
                      onChange={(e) => setDispatchChannels({ ...dispatchChannels, email: e.target.checked })}
                      className="mt-0.5" />
                    <div className="flex-1">
                      <div className="font-medium text-sm flex items-center gap-2"><Mail size={14} /> Email</div>
                      <div className="text-[10px] text-white/45 font-mono mt-0.5">
                        → {sendDispatch.customer?.email_contratti || sendDispatch.customer?.email || <span className="text-amber-300">⚠ email mancante in anagrafica</span>}
                      </div>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/10 cursor-pointer hover:border-emerald-500/40">
                    <input type="checkbox" data-testid="dispatch-wa-toggle" checked={dispatchChannels.whatsapp}
                      onChange={(e) => setDispatchChannels({ ...dispatchChannels, whatsapp: e.target.checked })}
                      className="mt-0.5" />
                    <div className="flex-1">
                      <div className="font-medium text-sm flex items-center gap-2"><MessageCircle size={14} /> WhatsApp</div>
                      <div className="text-[10px] text-white/45 font-mono mt-0.5">
                        → {sendDispatch.customer?.whatsapp_contratti || sendDispatch.customer?.telefono || <span className="text-amber-300">⚠ telefono mancante</span>}
                      </div>
                    </div>
                  </label>
                </div>

                <div className="flex gap-3 justify-end">
                  <button onClick={() => setSendDispatch(null)} disabled={dispatchBusy} className="px-5 py-2.5 rounded-full text-sm bg-white/[0.04] border border-white/10">Annulla</button>
                  <button onClick={doDispatch} disabled={dispatchBusy} data-testid="dispatch-confirm"
                    className="btn-neon px-6 py-2.5 rounded-full text-sm inline-flex items-center gap-2">
                    {dispatchBusy ? <><Loader2 size={14} className="animate-spin" /> Generazione...</> : <><Send size={14} /> Genera OTP e invia</>}
                  </button>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-[#ff0033]/10 border border-[#ff0033]/40">
                  <div className="text-[10px] uppercase tracking-widest text-white/45 mb-1">Codice OTP (valido 30 min)</div>
                  <div className="font-display font-black text-4xl tracking-[0.3em] text-[#ff5577]" data-testid="dispatch-otp">{dispatchResult.otp}</div>
                  <div className="text-[10px] text-white/40 mt-2">Comunica questo codice al cliente. Il cliente lo inserirà nella pagina firma per autenticarsi.</div>
                </div>

                <div className="text-xs">
                  <div className="text-white/45 uppercase tracking-widest mb-1 text-[10px]">Link firma pubblico</div>
                  <code className="block bg-black/40 border border-white/10 rounded px-2 py-1 text-[10px] break-all">{dispatchResult.sign_link}</code>
                  <button onClick={() => { navigator.clipboard.writeText(dispatchResult.sign_link); toast.success("Link copiato"); }}
                    className="mt-2 text-[10px] uppercase tracking-wider text-[#ff5577] hover:underline">Copia link</button>
                </div>

                {dispatchResult.channels?.email && (
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10 text-xs">
                    <div className="flex items-center gap-2 font-medium mb-1"><Mail size={12} /> Email</div>
                    {dispatchResult.channels.email.sent && <div className="text-emerald-300">✓ Inviata a {dispatchResult.channels.email.recipient}</div>}
                    {dispatchResult.channels.email.mailto && (
                      <a href={dispatchResult.channels.email.mailto} className="inline-block mt-1 text-blue-300 hover:underline">→ Apri Email (mailto)</a>
                    )}
                    {dispatchResult.channels.email.error && <div className="text-amber-300 mt-1">⚠ {dispatchResult.channels.email.error}</div>}
                  </div>
                )}

                {dispatchResult.channels?.whatsapp?.share_url && (
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10 text-xs">
                    <div className="flex items-center gap-2 font-medium mb-1"><MessageCircle size={12} /> WhatsApp</div>
                    <a href={dispatchResult.channels.whatsapp.share_url} target="_blank" rel="noopener noreferrer"
                      data-testid="dispatch-wa-open"
                      className="inline-block px-4 py-2 rounded-full text-xs bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/25 mt-1">
                      → Apri WhatsApp e invia
                    </a>
                  </div>
                )}

                <button onClick={() => { setSendDispatch(null); load(); }}
                  className="btn-neon w-full py-2.5 rounded-full text-sm">Fine</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6" onClick={onClose}>
    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md glass-strong rounded-3xl p-7">
      <div className="font-display text-xl font-bold mb-5 flex items-center gap-2"><Calendar size={18} className="text-[#ff0033]" />{title}</div>
      {children}
    </div>
  </div>
);
